import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Users, ShieldCheck, Wallet, Settings, AlertCircle } from "lucide-react";

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!stats) return null;

  return (
    <div className="z-container" data-testid="admin-dashboard-page">
      <h1 className="z-page-title">Tableau de bord plateforme</h1>
      <p className="z-page-sub">Bonjour, {user?.name} — voici l'activité de Zevents.</p>

      {stats.pending_moderation.total > 0 && (
        <Link to="/admin/moderation" data-testid="admin-pending-alert" style={{ textDecoration: "none" }}>
          <div style={{ background: "rgba(218,113,1,0.10)", color: "var(--z-orange)", padding: 14, borderRadius: 12, marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
            <AlertCircle size={22} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700 }}>
                {stats.pending_moderation.total} élément(s) en attente de validation
              </div>
              <div style={{ fontSize: 13 }}>
                {stats.pending_moderation.events} événements · {stats.pending_moderation.fundraisers} cagnottes · {stats.pending_moderation.votes} votes · {stats.pending_moderation.surveys} sondages
              </div>
            </div>
            <span className="z-btn z-btn-primary z-btn-sm">Examiner</span>
          </div>
        </Link>
      )}

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: "16px 0 8px", color: "var(--z-muted)" }}>Utilisateurs</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 18 }}>
        <Kpi label="Total" value={stats.users.total} testId="kpi-users-total" />
        <Kpi label="Acheteurs" value={stats.users.buyers} testId="kpi-users-buyers" />
        <Kpi label="Organisateurs" value={stats.users.organizers} testId="kpi-users-organizers" />
        <Kpi label="Agents" value={stats.users.agents} testId="kpi-users-agents" />
        <Kpi label="Suspendus" value={stats.users.suspended} testId="kpi-users-suspended" />
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: "16px 0 8px", color: "var(--z-muted)" }}>Événements & billets</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 18 }}>
        <Kpi label="Événements" value={stats.events.total} testId="kpi-events-total" />
        <Kpi label="Publiés" value={stats.events.published} testId="kpi-events-published" />
        <Kpi label="En attente" value={stats.events.pending} testId="kpi-events-pending" />
        <Kpi label="Billets vendus" value={stats.tickets.sold} testId="kpi-tickets-sold" />
        <Kpi label="Scannés" value={stats.tickets.scanned} testId="kpi-tickets-scanned" />
      </div>

      <h3 style={{ fontSize: 15, fontWeight: 700, margin: "16px 0 8px", color: "var(--z-muted)" }}>Revenus plateforme</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, marginBottom: 24 }}>
        <Kpi label="Revenus billets" value={`${(stats.revenue_total || 0).toLocaleString("fr-FR")}`} testId="kpi-revenue" />
        <Kpi label="Récolté cagnottes" value={`${(stats.raised_total || 0).toLocaleString("fr-FR")}`} testId="kpi-raised" />
      </div>

      <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Actions rapides</h3>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12 }}>
        <Link to="/admin/moderation" className="z-role-card" data-testid="quick-moderation">
          <div className="z-role-icon"><ShieldCheck size={22} /></div>
          <h3>Modération</h3>
          <p>Valider ou rejeter les publications.</p>
        </Link>
        <Link to="/admin/users" className="z-role-card" data-testid="quick-users">
          <div className="z-role-icon"><Users size={22} /></div>
          <h3>Utilisateurs</h3>
          <p>Gérer comptes & rôles.</p>
        </Link>
        <Link to="/admin/payouts" className="z-role-card" data-testid="quick-payouts">
          <div className="z-role-icon"><Wallet size={22} /></div>
          <h3>Paiements</h3>
          <p>Reverser aux organisateurs.</p>
        </Link>
        <Link to="/admin/settings" className="z-role-card" data-testid="quick-settings">
          <div className="z-role-icon"><Settings size={22} /></div>
          <h3>Paramètres</h3>
          <p>Commission & devises.</p>
        </Link>
      </div>
    </div>
  );
}

function Kpi({ label, value, testId }) {
  return (
    <div className="z-kpi" data-testid={testId}>
      <div className="z-kpi-label">{label}</div>
      <div className="z-kpi-value">{value}</div>
    </div>
  );
}
